import { useState, useCallback, useRef } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import { useAuth } from "@/contexts/AuthContext";
import { useRealtimeTable } from "./useRealtime";
import { formatError } from "@/lib/utils";

export type Attachment = {
  type: "image" | "document";
  media_type: string;
  data: string; // base64
  name: string;
};

export type ChatMessage = {
  role: "user" | "assistant";
  content: string;
  attachments?: Attachment[];
};

// How much history rides along to the chat edge fn per request. The DB
// keeps the whole thread; this only bounds the model context payload.
const HISTORY_WINDOW = 30;

// Persistent global chat (2026-06-10): the thread lives in
// global_chat_messages, not React state — closing the panel, navigating,
// or switching devices no longer loses it. User messages persist the
// moment they're sent (so even a timed-out request keeps your side of the
// conversation); assistant replies persist when they arrive. Only the
// explicit Clear action deletes the thread. Realtime keeps a phone and a
// desktop looking at the same conversation.
export function useChat() {
  const [pendingMessages, setPendingMessages] = useState<ChatMessage[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const { user } = useAuth();
  const queryClient = useQueryClient();
  // Tracks the active request so clear/new-send can invalidate stale responses
  const requestId = useRef(0);

  useRealtimeTable("global_chat_messages", ["global-chat"]);

  const { data: savedMessages } = useQuery({
    queryKey: ["global-chat", user?.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("global_chat_messages")
        .select("role, content")
        .eq("user_id", user!.id)
        .order("created_at")
        .limit(500);
      if (error) throw error;
      return (data || []) as ChatMessage[];
    },
    enabled: !!user,
    staleTime: 10_000,
  });

  // Realtime can land the just-persisted user row in savedMessages while
  // it's still sitting in pendingMessages — dedupe against the saved tail
  // so the bubble doesn't render twice mid-request.
  const saved = savedMessages || [];
  const savedTail = new Set(
    saved.slice(-10).map((m) => `${m.role} ${m.content}`),
  );
  const visiblePending = pendingMessages.filter(
    (m) => !savedTail.has(`${m.role} ${m.content}`),
  );
  const messages: ChatMessage[] = [...saved, ...visiblePending];

  const sendMessage = useCallback(
    async (text: string, attachments?: Attachment[]) => {
      if (!user) return;

      const userMessage: ChatMessage = {
        role: "user",
        content: text,
        attachments,
      };
      setPendingMessages((prev) => [...prev, userMessage]);
      setIsLoading(true);

      const thisRequest = ++requestId.current;
      const history = [...(savedMessages || []), ...pendingMessages, userMessage];

      // Persist the user side immediately — a slow/failed model response
      // must not eat what you typed. Fire-and-forget; the thread query
      // reconciles via invalidation below.
      const persistUser = supabase
        .from("global_chat_messages")
        .insert({ user_id: user.id, role: "user", content: text });

      try {
        const { data, error } = await Promise.race([
          supabase.functions.invoke("chat", {
            body: {
              messages: history.slice(-HISTORY_WINDOW).map((m) => ({
                role: m.role,
                content: m.content,
              })),
              user_id: user.id,
              // Send attachments only for the current message
              attachments: attachments?.map((a) => ({
                type: a.type,
                media_type: a.media_type,
                data: a.data,
              })),
            },
          }),
          new Promise<never>((_, reject) =>
            setTimeout(
              () => reject(new Error("Chat timed out — please try again.")),
              180_000
            )
          ),
        ]);

        await persistUser;

        // Chat was cleared or a new message sent — discard this stale response
        if (requestId.current !== thisRequest) return;

        if (error) throw error;

        const assistantContent: string =
          data.response || "I couldn't process that. Try again.";
        await supabase
          .from("global_chat_messages")
          .insert({ user_id: user.id, role: "assistant", content: assistantContent });

        setPendingMessages([]);
        queryClient.invalidateQueries({ queryKey: ["global-chat"] });
        queryClient.invalidateQueries({ queryKey: ["items"] });
        queryClient.invalidateQueries({ queryKey: ["item-stats"] });
      } catch (err) {
        await persistUser.then(
          () => queryClient.invalidateQueries({ queryKey: ["global-chat"] }),
          () => {},
        );
        if (requestId.current !== thisRequest) return;
        // Errors stay ephemeral — they describe this attempt, not the thread.
        setPendingMessages((prev) => [
          ...prev.filter((m) => m !== userMessage),
          { role: "assistant", content: `Error: ${formatError(err)}` },
        ]);
      } finally {
        if (requestId.current === thisRequest) {
          setIsLoading(false);
        }
      }
    },
    [savedMessages, pendingMessages, user, queryClient]
  );

  const clearChat = useCallback(async () => {
    requestId.current++; // Invalidate any in-flight request
    setPendingMessages([]);
    setIsLoading(false);
    if (user) {
      await supabase.from("global_chat_messages").delete().eq("user_id", user.id);
      queryClient.invalidateQueries({ queryKey: ["global-chat"] });
    }
  }, [user, queryClient]);

  return { messages, isLoading, sendMessage, clearChat };
}
